/**
 * DOCUMENTO DE IDENTIDAD: varias identificaciones aceptadas (Omnicanal)
 * =====================================================================
 * Un documento del catálogo (típicamente «INE») puede aceptar varias
 * identificaciones oficiales: settings.identificaciones = ['INE', 'PASAPORTE',
 * 'LICENCIA'] (en el orden en que se ofrecen). Sin configurar = como siempre.
 *
 * El colaborador elige cuál sube (la liga manda `identificacion` en cada
 * archivo) y queda en FlowData.identificacion. Según la elegida:
 *   INE        los archivos del documento (p. ej. Anverso/Reverso); recuadro de credencial
 *   PASAPORTE  1 archivo: «Hoja de datos»; recuadro de pasaporte (ID-3)
 *   LICENCIA   2 archivos: «Frente», «Reverso»; recuadro de credencial
 * Sin rangos: el mínimo es la cantidad de archivos. Un documento de identidad
 * siempre va por files[] (con fileIndex), aunque pida un archivo.
 *
 * ESTA REGLA ES LA MISMA en pulpi-v4-app-backend (app/services/identificaciones.js),
 * pulpi-v4-backend (app/helpers/identificaciones.js), el panel
 * (omnichannel-config/data/helpers/identificaciones.ts) y la liga
 * (verify-information/data/constants/identificaciones.ts), con la tabla
 * identificaciones.casos.json. Puro.
 */

export type Identificacion = 'INE' | 'PASAPORTE' | 'LICENCIA';
export type TipoArchivoIdentidad = 'INE_FRONT' | 'INE_BACK' | 'PASSPORT' | 'LICENSE_FRONT' | 'LICENSE_BACK';

export const IDENTIFICACIONES: readonly Identificacion[] = ['INE', 'PASAPORTE', 'LICENCIA'];

export const NOMBRE_IDENTIFICACION: Record<Identificacion, string> = {
  INE: 'INE',
  PASAPORTE: 'Pasaporte',
  LICENCIA: 'Licencia de conducir',
};

/** Archivos fijos por identificación; la INE usa los del documento. */
export const ARCHIVOS_IDENTIFICACION: Partial<Record<Identificacion, string[]>> = {
  PASAPORTE: ['Hoja de datos'],
  LICENCIA: ['Frente', 'Reverso'],
};

function leerSettings(settings: unknown): Record<string, unknown> {
  if (settings && typeof settings === 'object') return settings as Record<string, unknown>;
  if (typeof settings !== 'string' || !settings.trim()) return {};
  try {
    const v = JSON.parse(settings);
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

const normalizar = (valor: unknown): string => String(valor ?? '').trim().toUpperCase();

/** Las identificaciones que acepta el documento, sin repetir y en su orden; [] = no es de identidad. */
export function identificacionesDe(settings: unknown): Identificacion[] {
  const lista = leerSettings(settings)['identificaciones'];
  if (!Array.isArray(lista)) return [];
  const vistas: Identificacion[] = [];
  for (const v of lista.map(normalizar)) {
    if ((IDENTIFICACIONES as readonly string[]).includes(v) && !vistas.includes(v as Identificacion)) vistas.push(v as Identificacion);
  }
  return vistas;
}

export const esDocumentoDeIdentidad = (settings: unknown): boolean => identificacionesDe(settings).length > 0;

/** La identificación pedida si el documento la acepta; null si no. */
export function identificacionAceptada(settings: unknown, valor: unknown): Identificacion | null {
  const v = normalizar(valor);
  return (identificacionesDe(settings) as string[]).includes(v) ? (v as Identificacion) : null;
}

export interface IArchivosIdentidad {
  fileCount: number;
  fileLabels: string[];
  fileCountMin: number;
}

/** Los archivos que pide la identificación elegida; `base` = lo que ya resolvió el paso. Sin elegida = base. */
export function archivosDeIdentificacion(
  identificacion: unknown,
  base: { fileCount?: number | null; fileLabels?: string[] | null; fileCountMin?: number | null } | null | undefined
): IArchivosIdentidad {
  const fileCount = Number(base?.fileCount) > 0 ? Number(base!.fileCount) : 1;
  const fileLabels = Array.isArray(base?.fileLabels) ? base!.fileLabels! : [];
  const fijas = ARCHIVOS_IDENTIFICACION[normalizar(identificacion) as Identificacion];
  if (fijas) return {fileCount: fijas.length, fileLabels: [...fijas], fileCountMin: fijas.length};
  if (normalizar(identificacion) === 'INE') return {fileCount, fileLabels: [...fileLabels], fileCountMin: fileCount};
  return {fileCount, fileLabels: [...fileLabels], fileCountMin: Number(base?.fileCountMin) > 0 ? Number(base!.fileCountMin) : fileCount};
}

const sinAcentos = (t: unknown): string => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const esReverso = (etiqueta: unknown): boolean => /revers|tras|back|posterior/.test(sinAcentos(etiqueta));
const esFrente = (etiqueta: unknown): boolean => /frent|front|anvers|delanter/.test(sinAcentos(etiqueta));

/** Cómo se analiza el archivo `index` (la cara sale de la etiqueta; si no, el segundo es el reverso). */
export function tipoDelArchivo(identificacion: unknown, fileLabels: string[] | null | undefined, index: number): TipoArchivoIdentidad {
  const id = normalizar(identificacion);
  if (id === 'PASAPORTE') return 'PASSPORT';
  const etiqueta = Array.isArray(fileLabels) ? fileLabels[index] : '';
  const reverso = esReverso(etiqueta) ? true : esFrente(etiqueta) ? false : index === 1;
  if (id === 'LICENCIA') return reverso ? 'LICENSE_BACK' : 'LICENSE_FRONT';
  return reverso ? 'INE_BACK' : 'INE_FRONT';
}

/** Recuadro de la cámara de la liga para la identificación. */
export const guiaDeIdentificacion = (identificacion: unknown): 'TARJETA' | 'PASAPORTE' =>
  (normalizar(identificacion) === 'PASAPORTE' ? 'PASAPORTE' : 'TARJETA');
